import React from "react";
import { PickersDay } from "@mui/x-date-pickers";
import { Tooltip } from "@mui/material";

const AvailabilityPickersDay = PickersDay as React.ElementType;

function RRDMailingDateAvailabilityDays({
  day,
  selectedDay,
  hoveredDay,
  onHover,
  highlightWeekDaysOnly = false,
  weeklyAvailabilityData = {},
  ...other
}) {
  // Week selection logic (Monday to Friday when highlightWeekDaysOnly is true)
  const getRange = (baseDate) => {
    if (!baseDate) return null;
    const startOfWeekMoment = baseDate.clone().startOf("week"); // Sunday

    if (highlightWeekDaysOnly) {
      // Monday to Friday
      return {
        start: startOfWeekMoment.clone().add(1, "days"),
        end: startOfWeekMoment.clone().add(5, "days"),
      };
    }
    // Sunday to Saturday
    return {
      start: startOfWeekMoment,
      end: baseDate.clone().endOf("week"),
    };
  };

  const range = getRange(day);
  const isFirstDay = range && day.isSame(range.start, "day");
  const isLastDay = range && day.isSame(range.end, "day");

  let dayIsBetween = false;
  let isHovered = false;

  if (selectedDay) {
    const { start, end } = getRange(selectedDay);
    dayIsBetween = day.isBetween(start, end, "day", "[]");
  }

  if (hoveredDay) {
    const { start, end } = getRange(hoveredDay);
    isHovered = day.isBetween(start, end, "day", "[]");
  }

  // Get availability data for the week containing this day (Sunday start)
  const weekStart = day.clone().startOf("week");
  const weekKey = weekStart.format("YYYY-MM-DD");
  const availabilityData = weeklyAvailabilityData[weekKey] || null;
  const weekColor = availabilityData?.color || null;
  const tooltipText = availabilityData?.tooltip || null;

  // Check if this day is within the Monday-Friday range for availability display
  const isInWeekRange = highlightWeekDaysOnly
    ? day.day() >= 1 && day.day() <= 5 // Monday (1) to Friday (5)
    : true; // All days if not highlighting week days only

  const availabilityBgColor = weekColor && isInWeekRange ? weekColor : null;

  const dayComponent = (
    <AvailabilityPickersDay
      {...other}
      day={day}
      selected={dayIsBetween}
      dayIsBetween={dayIsBetween}
      isFirstDay={isFirstDay}
      isLastDay={isLastDay}
      isHovered={isHovered}
      disableMargin
      onMouseEnter={() => onHover && onHover(day)}
      onMouseLeave={() => onHover && onHover(null)}
      autoFocus={false}
      sx={(theme) => {
        const baseStyles = {
          position: "relative",
          borderRadius: "initial",
          width: 40,
          height: 40,
        };

        // Selected week styles (Monday-Friday when highlightWeekDaysOnly is true)
        if (dayIsBetween) {
          return {
            ...baseStyles,
            backgroundColor: theme.palette.primary.light,
            color: theme.palette.common.white,
            "&:hover, &:focus, &.Mui-selected": {
              backgroundColor: theme.palette.primary.light,
              color: theme.palette.common.white,
            },
            ...(isFirstDay && {
              borderTopLeftRadius: "var(--radius-round)",
              borderBottomLeftRadius: "var(--radius-round)",
            }),
            ...(isLastDay && {
              borderTopRightRadius: "var(--radius-round)",
              borderBottomRightRadius: "var(--radius-round)",
            }),
          };
        }

        // Hover styles
        if (isHovered) {
          return {
            ...baseStyles,
            backgroundColor: theme.palette.primary.main + "1A",
            "&:hover, &:focus": {
              backgroundColor: theme.palette.primary.main + "1A",
            },
            ...(isFirstDay && {
              borderTopLeftRadius: "var(--radius-round)",
              borderBottomLeftRadius: "var(--radius-round)",
            }),
            ...(isLastDay && {
              borderTopRightRadius: "var(--radius-round)",
              borderBottomRightRadius: "var(--radius-round)",
            }),
          };
        }

        if (availabilityBgColor && isInWeekRange) {
          return {
            ...baseStyles,
            backgroundColor: availabilityBgColor,
            color: theme.palette.text.primary,
            "&.Mui-selected": {
              backgroundColor: availabilityBgColor + " !important",
              color: theme.palette.text.primary,
            },
            "&:hover": {
              backgroundColor: availabilityBgColor,
            },
            ...(isFirstDay && {
              borderTopLeftRadius: "var(--radius-round)",
              borderBottomLeftRadius: "var(--radius-round)",
            }),
            ...(isLastDay && {
              borderTopRightRadius: "var(--radius-round)",
              borderBottomRightRadius: "var(--radius-round)",
            }),
          };
        }

        // Default styles
        return {
          ...baseStyles,
          "&.Mui-selected": {
            backgroundColor: "transparent !important",
            color: theme.palette.text.primary,
          },
          "&:focus, &.Mui-focusVisible": {
            backgroundColor: "transparent !important",
            color: theme.palette.text.primary,
          },
        };
      }}
    />
  );

  // Show tooltip if availability data exists (for Monday-Friday days)
  if (!tooltipText || !isInWeekRange) {
    return dayComponent;
  }

  return (
    <Tooltip
      title={<strong>{tooltipText}</strong>}
      arrow
      placement="top"
      slotProps={{
        popper: { disablePortal: false, style: { zIndex: 99999 } },
        tooltip: {
          sx: {
            backgroundColor: "var(--neutral-white)",
            color: "var(--palette-text-primary)",
            fontSize: "9px",
            padding: "6px 12px",
            boxShadow: "var(--shadow-floating)",
            "& .MuiTooltip-arrow": { color: "var(--neutral-white)" },
          },
        },
      }}
    >
      {dayComponent}
    </Tooltip>
  );
}

export default RRDMailingDateAvailabilityDays;
